/**
 * FileInfo Component
 *
 * Displays information about the selected file.
 */

import { memo } from 'react';
import { FileAudio, FileVideo, File } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';
import { formatFileSize } from '@/constants/fileSize';

interface FileInfoProps {
  file: File;
  mediaType: 'audio' | 'video';
}

function getFileExtension(filename: string): string {
  return filename.split('.').pop()?.toUpperCase() || 'FILE';
}

export const FileInfo = memo(function FileInfo({ file, mediaType }: FileInfoProps) {
  const { t } = useTranslation();

  const Icon = mediaType === 'video' ? FileVideo : FileAudio;
  const iconColor = mediaType === 'video' ? 'text-blue-500' : 'text-purple-500';
  const bgColor =
    mediaType === 'video' ? 'bg-blue-50 dark:bg-blue-900/20' : 'bg-purple-50 dark:bg-purple-900/20';

  return (
    <div className="p-4 sm:p-6 bg-white dark:bg-secondary-800 rounded-lg border border-secondary-200 dark:border-secondary-700">
      <h3 className="text-sm font-medium text-secondary-500 dark:text-secondary-400 mb-3">
        {t('converter.fileInfo.title', { defaultValue: 'Selected File' })}
      </h3>

      <div className="flex items-start gap-4">
        {/* Icon */}
        <div className={`p-3 rounded-lg ${bgColor}`}>
          <Icon className={`w-8 h-8 ${iconColor}`} />
        </div>

        {/* File details */}
        <div className="flex-1 min-w-0">
          <p className="text-lg font-medium text-secondary-900 dark:text-white truncate">
            {file.name}
          </p>
          <div className="flex flex-wrap gap-3 mt-2 text-sm text-secondary-600 dark:text-secondary-400">
            <span className="flex items-center gap-1">
              <File className="w-4 h-4" />
              {getFileExtension(file.name)}
            </span>
            <span>|</span>
            <span>{formatFileSize(file.size)}</span>
            <span>|</span>
            <span className="capitalize">{mediaType}</span>
          </div>
        </div>
      </div>
    </div>
  );
});

export default FileInfo;
